<template>
  <div class="p-relative">
    <div class="ml-n5 mr-n5">
      <chart :options="chartOptions" />
    </div>
    <div class="mt-n4">
      <v-divider class="border-vault"></v-divider>
    </div>
    <div
      class="flex-1 d-flex flex-column flex-sm-row justify-sm-end mt-5 p-relative z-index-1 text-6"
    >
      <div class="d-flex justify-end">
        <div
          :class="['btn-badge vault ml-3', { active: chartType === item.name }]"
          v-for="item in chartTypes"
          :key="item.name"
          @click="chartType = item.name"
        >
          {{ item.text }}
        </div>
      </div>
      <v-divider
        vertical
        class="border-color-primary opacity-1 mx-5 d-none d-sm-block"
      ></v-divider>
      <div class="d-flex justify-end mt-3 mt-sm-0">
        <div
          v-for="per in periods"
          @click="selectQueryTime(per.name)"
          :key="per.name"
          :class="[
            'clickable btn-badge vault',
            {
              'mr-3': !!per.days,
              active: per.name === period,
            },
          ]"
        >
          {{ per.name }}
        </div>
      </div>
    </div>
    <div
      class="p-absolute no-data p-14-r gray--text d-flex align-center justify-center"
      v-if="!seriesData || seriesData.length === 0"
    >
      No data to display
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Chart } from "highcharts-vue";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import startOfYear from "date-fns/startOfYear";
import sub from "date-fns/sub";
import formatDate from "@/filters/dateTime.filter";
import numberFormat from "@/filters/numberFormat.filter";

export default {
  components: {
    Chart,
  },
  props: {
    vaultInfo: Object,
  },
  data: () => ({
    chartType: "tvl",
    chartTypes: [
      {
        name: "tvl",
        text: "TVL",
      },
      {
        name: "apy",
        text: "APY",
      },
    ],
    period: "1W",
    periods: [
      {
        name: "1W",
        days: differenceInCalendarDays(
          new Date(),
          sub(new Date(), { weeks: 1 })
        ),
      },
      {
        name: "1M",
        days: differenceInCalendarDays(
          new Date(),
          sub(new Date(), { months: 1 })
        ),
      },
      {
        name: "3M",
        days: differenceInCalendarDays(
          new Date(),
          sub(new Date(), { months: 3 })
        ),
      },
      {
        name: "6M",
        days: differenceInCalendarDays(
          new Date(),
          sub(new Date(), { months: 6 })
        ),
      },
      {
        name: "1Y",
        days: differenceInCalendarDays(
          new Date(),
          sub(new Date(), { years: 1 })
        ),
      },
      {
        name: "YTD",
        days: differenceInCalendarDays(new Date(), startOfYear(new Date())),
      },
      {
        name: "MAX",
        days: undefined,
      },
    ],
    downloading: false,
    chartData: {},
  }),
  computed: {
    ...mapGetters({
      currentProject: "projectConfig/latestProject",
    }),
    queryTime() {
      return this.periods.find(pr => pr.name === this.period).days;
    },
    seriesData() {
      return this.chartData || [];
    },
    chartOptions() {
      const chartType = this.chartType;
      return {
        chart: {
          backgroundColor: "transparent",
          type: "area",
          height: 200,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: "",
        },
        subtitle: {
          text: "",
        },
        xAxis: {
          type: "datetime",
          visible: false,
        },
        yAxis: {
          visible: false,
          minorTickInterval: "auto",
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          crosshairs: [true],
          useHTML: true,
          borderRadius: 10,
          backgroundColor: this.$vuetify.theme.dark ? "#222E3B" : "#FFFFFF",
          formatter() {
            return `<div class="text-center px-5">
                <div class="text-5 line-height-25 font-weight-medium black--text">
                  <span>${chartType === "tvl" ? "$" : ""}${numberFormat(
              this.y,
              "0,0.00",
              false
            )}${chartType === "apy" ? "%" : ""}</span>
                </div>
                <div class="text-6 gray--text">${formatDate(
                  this.x,
                  "MMM dd, yyyy"
                )}</div>
             </div>`;
          },
        },
        colors: ["#49D999"],
        plotOptions: {
          series: {
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1,
              },
              stops: [
                [0, "#49D9991A"],
                [1, this.$vuetify.theme.dark ? "#222E3B33" : "#FFFFFF33"],
              ],
            },
            marker: {
              radius: 0,
            },
            lineWidth: 1,
            states: {
              hover: {
                lineWidth: 0,
              },
            },
            threshold: null,
          },
        },
        series: {
          type: "area",
          data: this.seriesData,
        },
      };
    },
  },
  methods: {
    selectQueryTime(period) {
      this.period = period;
      this.loadData();
    },
    async loadApyData() {
      this.chartData = await this.$store.dispatch(
        `vaults/fetchVaultsHistoricalApy`,
        {
          time: this.periods.find(pr => pr.name === this.period).days,
          type: this.vaultInfo.type,
          token: this.vaultInfo.token,
        }
      );
    },
    async loadTvlData() {
      const result = await this.$store.dispatch(
        "productStats/fetchHistoricalVolume",
        {
          days: this.periods.find(pr => pr.name === this.period).days,
        }
      );
      this.chartData = result.details.vaults[this.vaultInfo.vault];
    },
    async loadData() {
      switch (this.chartType) {
        case "tvl": {
          return this.loadTvlData();
        }
        case "apy": {
          return this.loadApyData();
        }
        default:
          return;
      }
    },
  },
  mounted() {
    this.loadData();
  },
  watch: {
    chartType() {
      this.loadData();
    },
  },
};
</script>

<style lang="scss" scoped>
.no-data {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
</style>
